import useLocalStorage from '@rehooks/local-storage'
import { useAcessoLotacao } from 'components/auth/useAcessoLotacao'
import useSession from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { PageLoading } from 'components/loading'
import { PecSwitch } from 'components/route'
import { useEscutaInicialViewQuery } from 'graphql/hooks.generated'
import { StatusAtendimento, TipoAtendimentoProfissional } from 'graphql/types.generated'
import React from 'react'
import { Route, useParams, useRouteMatch } from 'react-router'
import { Redirect } from 'react-router-dom'

import Permissions from '../../../types/Permissions'
import { AtendimentoHeader, AtendimentoHeaderProps } from '../detail/AtendimentoHeader'
import { CidadaoDetailView } from '../detail/cadastro-cidadao/CidadaoDetailView'
import { FolhaRostoView } from '../detail/folha-rosto/FolhaRostoView'
import { EscutaInicialMainView } from './EscutaInicialMainView'
import { EscutaInicialState, initialValue } from './model'

interface EscutaInicialViewProps {
  atendimentoId: ID
}

export function EscutaInicialView() {
  const { atendimentoId } = useParams<EscutaInicialViewProps>()
  const match = useRouteMatch()
  const acesso = useAcessoLotacao()
  const { hasAuthorization } = useSession()
  const canCreateNovoAtendimento = hasAuthorization(Permissions.visualizarListaDeAtendimento.cadastrarEditarEExcluir)

  const {
    data: { atendimento, procedimentosAutomaticos },
    loading,
  } = useEscutaInicialViewQuery({
    fetchPolicy: 'cache-and-network',
    variables: { atendimentoId },
  })

  const [cacheState, setCacheState, deleteCacheState] = useLocalStorage<EscutaInicialState>(
    `${acesso.id}/atendimento/escuta-inicial/${atendimentoId}`,
    initialValue(atendimento?.tiposServico, canCreateNovoAtendimento)
  )

  const statusInvalido =
    atendimento?.statusAtendimento === StatusAtendimento.ATENDIMENTO_REALIZADO ||
    atendimento?.statusAtendimento === StatusAtendimento.NAO_AGUARDOU

  const tipoValido =
    atendimento?.atendimentoProfissional?.tipo === TipoAtendimentoProfissional.ESCUTA_INICIAL ||
    atendimento?.atendimentoProfissional?.tipo === TipoAtendimentoProfissional.PRE_ATENDIMENTO

  if (loading) {
    return <PageLoading message='Carregando atendimento...' />
  } else if (statusInvalido || !tipoValido) {
    return <Redirect to='/404' />
  } else if (atendimento?.atendimentoProfissional?.lotacao.id !== acesso?.id) {
    return <Redirect to='/403' />
  }

  const renderEscutaMainView = () => (
    <EscutaInicialMainView
      atendimentoId={atendimentoId}
      cidadao={atendimento?.cidadao}
      isPreAtendimento={!!atendimento?.agendado}
      cacheState={cacheState}
      updateCache={setCacheState}
      clearCache={deleteCacheState}
      procedimentosAutomaticos={procedimentosAutomaticos || []}
    />
  )
  const renderCidadaoDetail = () => (
    <CidadaoDetailView
      atendimentoId={atendimentoId}
      cidadaoId={atendimento?.cidadao?.id}
      clearCache={deleteCacheState}
    />
  )
  const renderFolhaRostoView = () => <FolhaRostoView atendimentoId={atendimentoId} clearCache={deleteCacheState} />

  const headerProps: AtendimentoHeaderProps = {
    cidadaoId: atendimento?.cidadao?.id,
    renderFolhaRosto: true,
    renderSoap: false,
    renderVacinacao: false,
    renderHistorico: false,
    renderAntecedentes: false,
    renderCadastroCidadao: true,
    renderEscutaInicial: true,
  }

  const labelEscutaPreAtendimento = atendimento?.agendado ? 'Pré-atendimento' : 'Escuta inicial'

  return (
    <>
      <Breadcrumb title={labelEscutaPreAtendimento} />

      <AtendimentoHeader labelEscutaPreAtendimento={labelEscutaPreAtendimento} {...headerProps} />

      <PecSwitch>
        <Redirect exact path={match.url} to={`${match.url}/escuta-inicial`} />
        <Route path={`${match.url}/escuta-inicial`} render={renderEscutaMainView} />
        <Route path={`${match.url}/folha-rosto`} component={renderFolhaRostoView} />
        <Route path={`${match.url}/cadastro-cidadao`} render={renderCidadaoDetail} />
      </PecSwitch>
    </>
  )
}
